import api from './axiosBase';

export interface Project {
  simpleId: string;
  projectName: string;
  shopName?: string;
  optimalMessageRangeMin?: number;
  optimalMessageRangeMax?: number;
  optimalAvatar?: {
    name: string;
    email: string;
    shopifyData?: {
      amountSpent: number;
      numberOfOrders: number;
    };
  };
}

export interface ReferenceCount {
  id: string;
  type: string;
  count: number;
}

export interface TopReferencesResponse {
  topProducts: ReferenceCount[];
  topDiscounts: ReferenceCount[];
}

export interface GetProjectOptions {
  includeCustomers?: boolean;
  customerLimit?: number;
  customerStartAfter?: string;
}

// In-flight request dedup: prevents duplicate concurrent calls for the same project
const _inflightProjects = new Map<
  string,
  Promise<Project & { nextCustomerCursor?: string }>
>();

export async function getProject(
  projectID: string,
  options: GetProjectOptions | boolean = {}
): Promise<Project & { nextCustomerCursor?: string }> {
  // Backward compatibility: if options is a boolean, treat it as includeCustomers
  const normalizedOptions: GetProjectOptions =
    typeof options === 'boolean' ? { includeCustomers: options } : options;

  const {
    includeCustomers = true,
    customerLimit,
    customerStartAfter
  } = normalizedOptions;

  // Build a cache key from the request params
  const dedupeKey = `${projectID}:${includeCustomers}:${customerLimit || ''}:${
    customerStartAfter || ''
  }`;

  // If an identical request is already in-flight, reuse it
  const inflight = _inflightProjects.get(dedupeKey);
  if (inflight) {
    return inflight;
  }

  const params: Record<string, string> = {
    includeCustomers: includeCustomers.toString()
  };
  if (customerLimit) {
    params.customerLimit = customerLimit.toString();
  }
  if (customerStartAfter) {
    params.customerStartAfter = customerStartAfter;
  }

  const promise = api
    .get(`/v2/project/${projectID}`, { params })
    .then((response) => response.data)
    .finally(() => {
      _inflightProjects.delete(dedupeKey);
    });

  _inflightProjects.set(dedupeKey, promise);
  return promise;
}

export async function getOptimalAvatar(
  projectID: string
): Promise<Project['optimalAvatar']> {
  const response = await api.get(`/v2/project/${projectID}/optimal-avatar`);
  return response.data.optimalAvatar;
}

export async function updateProject(
  projectID: string,
  data: Partial<Project>
): Promise<Project> {
  const response = await api.put(`/v2/project/${projectID}`, data);
  return response.data;
}

export async function getTopReferences(
  projectID: string
): Promise<TopReferencesResponse> {
  const response = await api.get(`/v2/project/${projectID}/top-references`);
  return response.data;
}

export interface SetActiveStatusResponse {
  success: boolean;
  isActive: boolean;
  activatedAt?: string;
}

export async function setActiveStatus(
  projectID: string,
  isActive: boolean
): Promise<SetActiveStatusResponse> {
  const response = await api.put(`/v2/project/${projectID}/active`, {
    isActive
  });
  return response.data;
}

export interface AIPopupSettings {
  enabled: boolean;
  resolveConflicts: boolean;
  personalize: boolean;
  frequency: 'light' | 'balanced' | 'aggressive';
}

export async function updatePopupSettings(
  projectID: string,
  settings: AIPopupSettings
): Promise<{ success: boolean; aiPopupSettings: AIPopupSettings }> {
  const response = await api.put(
    `/v2/project/${projectID}/popup-settings`,
    settings
  );
  return response.data;
}
